import React, { useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { participantsApi } from "../api/participants";
import { qk } from "../../../../lib/queryKeys";
import { ROUTES } from "../../../../shared/utils";
import { useClientScope } from "../../../../shared/contexts";
import { buildCsv, downloadCsv } from "../../utils/csv";

const STATUS_OPTIONS = ["Active", "Pending", "On hold", "Completed"];
const FLAGGED_OPTIONS = ["None", "Needs follow-up", "Escalated"];
const statusChipStyles: Record<string, { bg: string; text: string }> = {
  Active: { bg: "#dcfce7", text: "#166534" },
  Pending: { bg: "#fef9c3", text: "#854d0e" },
  "On hold": { bg: "#fee2e2", text: "#991b1b" },
  Completed: { bg: "#e0f2fe", text: "#075985" },
};

const wearStatusChipStyles: Record<string, { bg: string; text: string; label: string }> = {
  not_worn: { bg: "#fee2e2", text: "#991b1b", label: "Not worn" },
  intermittent_wear: { bg: "#fef3c7", text: "#92400e", label: "Intermittent wear" },
  not_connected: { bg: "#e5e7eb", text: "#374151", label: "Not connected" },
  recently_connected: { bg: "#dbeafe", text: "#1e3a8a", label: "Recently connected" },
  wearing_consistently: {
    bg: "#dcfce7",
    text: "#166534",
    label: "Wearing consistently",
  },
};

const getStatusChipSx = (status?: string | null) => {
  if (!status) return undefined;
  const palette = statusChipStyles[status];
  if (!palette) return undefined;
  return { backgroundColor: palette.bg, color: palette.text, fontWeight: 600 };
};

const formatDateTime = (value?: string | null) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString();
};

export const Participants: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isCentralAdmin, effectiveClientId } = useClientScope();
  const [search, setSearch] = useState(searchParams.get("search") ?? "");
  const [status, setStatus] = useState(searchParams.get("status") ?? "");
  const [flagged, setFlagged] = useState("");
  const [companyFilter, setCompanyFilter] = useState("");
  const [whoopReceived, setWhoopReceived] = useState<"" | "true" | "false">("");
  const [usingOwnDevice, setUsingOwnDevice] = useState<"" | "true" | "false">("");
  const [programmeState, setProgrammeState] = useState<"" | "on" | "off">("");
  const [headerFilters, setHeaderFilters] = useState({
    participant: "",
    linkedUser: "",
    company: "",
    status: searchParams.get("status") ?? "",
    wearJeopardy: "",
  });

  const queryParams = useMemo(
    () => ({
      client_id: effectiveClientId || undefined,
      search: search || undefined,
      status: status || undefined,
      flagged: flagged === "None" ? "" : flagged || undefined,
      whoop_received:
        whoopReceived === ""
          ? undefined
          : whoopReceived === "true",
      using_own_device:
        usingOwnDevice === ""
          ? undefined
          : usingOwnDevice === "true",
    }),
    [effectiveClientId, search, status, flagged, whoopReceived, usingOwnDevice]
  );

  const listQuery = useQuery({
    queryKey: qk.participants(queryParams),
    queryFn: () => participantsApi.list(queryParams),
  });
  const participants = listQuery.data?.data ?? [];

  const filteredParticipants = useMemo(() => {
    const normalize = (value?: string | null) => String(value || "").toLowerCase().trim();
    const isOnProgramme = (participant: (typeof participants)[number]) =>
      Boolean(
        participant.status ||
          participant.date_started ||
          participant.date_added_to_lms
      );

    return participants.filter((participant) => {
      if (
        companyFilter &&
        normalize(participant.company) !== normalize(companyFilter)
      ) {
        return false;
      }

      if (programmeState === "on" && !isOnProgramme(participant)) return false;
      if (programmeState === "off" && isOnProgramme(participant)) return false;

      const participantName = normalize(
        `${participant.first_name ?? ""} ${participant.surname ?? ""}`
      );
      const linkedUser = normalize(
        `${participant.user_first_name ?? ""} ${participant.user_last_name ?? ""} ${participant.user_email ?? ""}`
      );

      if (
        headerFilters.participant &&
        !participantName.includes(normalize(headerFilters.participant)) &&
        !normalize(participant.email).includes(normalize(headerFilters.participant))
      ) {
        return false;
      }

      if (
        headerFilters.linkedUser &&
        !linkedUser.includes(normalize(headerFilters.linkedUser))
      ) {
        return false;
      }

      if (
        headerFilters.company &&
        !normalize(participant.company).includes(normalize(headerFilters.company))
      ) {
        return false;
      }

      if (
        headerFilters.status &&
        normalize(participant.status) !== normalize(headerFilters.status)
      ) {
        return false;
      }

      if (
        headerFilters.wearJeopardy &&
        normalize(participant.wear_jeopardy_status) !==
          normalize(headerFilters.wearJeopardy)
      ) {
        return false;
      }

      return true;
    });
  }, [companyFilter, headerFilters, participants, programmeState]);

  const companyOptions = useMemo(() => {
    const unique = new Set(
      participants
        .map((participant) => (participant.company || "").trim())
        .filter(Boolean)
    );
    return Array.from(unique).sort((a, b) => a.localeCompare(b));
  }, [participants]);

  const companyCounts = useMemo(() => {
    const counts = new Map<string, number>();
    filteredParticipants.forEach((participant) => {
      const key = (participant.company || "Unassigned").trim() || "Unassigned";
      counts.set(key, (counts.get(key) ?? 0) + 1);
    });
    return Array.from(counts.entries()).sort((a, b) => b[1] - a[1]);
  }, [filteredParticipants]);

  const notOnProgrammeCount = useMemo(
    () =>
      filteredParticipants.filter(
        (participant) =>
          !participant.status &&
          !participant.date_started &&
          !participant.date_added_to_lms
      ).length,
    [filteredParticipants]
  );

  const handleDownloadParticipants = () => {
    if (!isCentralAdmin || !filteredParticipants.length) return;

    const csv = buildCsv(
      [
        "participant_name",
        "participant_email",
        "contact_number",
        "linked_user_email",
        "linked_user_name",
        "company",
        "status",
        "flagged",
        "color_tag",
        "wear_jeopardy_status",
        "whoop_received",
        "using_own_device",
        "updated_at",
      ],
      filteredParticipants.map((participant) => {
        const participantName =
          `${participant.first_name ?? ""} ${participant.surname ?? ""}`.trim();
        const linkedUserName =
          `${participant.user_first_name ?? ""} ${participant.user_last_name ?? ""}`.trim();
        return [
          participantName,
          participant.email ?? "",
          participant.contact_number ?? "",
          participant.user_email ?? "",
          linkedUserName,
          participant.company ?? "",
          participant.status ?? "",
          participant.flagged ?? "",
          participant.color_tag ?? "",
          participant.wear_jeopardy_status ?? "",
          participant.whoop_received ? "Yes" : "No",
          participant.using_own_device ? "Yes" : "No",
          participant.updated_at ?? "",
        ];
      }),
    );

    const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
    downloadCsv(`participants-${stamp}.csv`, csv);
  };

  return (
    <Box>
      <Stack spacing={3}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "center" }}>
          <Box flex={1}>
            <Typography variant="h4" fontWeight={600} gutterBottom>
              Participants
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Manage participant records, device readiness, and programme tracking.
            </Typography>
          </Box>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            {isCentralAdmin && (
              <Button
                variant="outlined"
                size="large"
                onClick={handleDownloadParticipants}
                disabled={listQuery.isLoading || filteredParticipants.length === 0}
              >
                Download filtered list
              </Button>
            )}
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate(`${ROUTES.PARTICIPANTS}/new`)}
            >
              New participant
            </Button>
          </Stack>
        </Stack>

        <Paper sx={{ p: 3 }} elevation={2}>
          <Stack spacing={2}>
            <Typography variant="subtitle1" fontWeight={600}>
              Filters
            </Typography>
            <Stack direction={{ xs: "column", lg: "row" }} spacing={2}>
              <TextField
                label="Search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Name, email, or company"
                fullWidth
              />
              <FormControl sx={{ minWidth: 180 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  label="Status"
                  value={status}
                  onChange={(event) => setStatus(event.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  {STATUS_OPTIONS.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl sx={{ minWidth: 180 }}>
                <InputLabel>Flagged</InputLabel>
                <Select
                  label="Flagged"
                  value={flagged}
                  onChange={(event) => setFlagged(event.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  {FLAGGED_OPTIONS.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl sx={{ minWidth: 220 }}>
                <InputLabel>Company</InputLabel>
                <Select
                  label="Company"
                  value={companyFilter}
                  onChange={(event) => setCompanyFilter(event.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  {companyOptions.map((company) => (
                    <MenuItem key={company} value={company}>
                      {company}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl sx={{ minWidth: 180 }}>
                <InputLabel>WHOOP received</InputLabel>
                <Select
                  label="WHOOP received"
                  value={whoopReceived}
                  onChange={(event) =>
                    setWhoopReceived(event.target.value as "" | "true" | "false")
                  }
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="true">Yes</MenuItem>
                  <MenuItem value="false">No</MenuItem>
                </Select>
              </FormControl>
              <FormControl sx={{ minWidth: 180 }}>
                <InputLabel>Using own device</InputLabel>
                <Select
                  label="Using own device"
                  value={usingOwnDevice}
                  onChange={(event) =>
                    setUsingOwnDevice(event.target.value as "" | "true" | "false")
                  }
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="true">Yes</MenuItem>
                  <MenuItem value="false">No</MenuItem>
                </Select>
              </FormControl>
              <FormControl sx={{ minWidth: 180 }}>
                <InputLabel>Programme</InputLabel>
                <Select
                  label="Programme"
                  value={programmeState}
                  onChange={(event) =>
                    setProgrammeState(event.target.value as "" | "on" | "off")
                  }
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="on">On programme</MenuItem>
                  <MenuItem value="off">Not on programme</MenuItem>
                </Select>
              </FormControl>
            </Stack>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Chip label={`Filtered participants: ${filteredParticipants.length}`} />
              <Chip label={`Not on programme: ${notOnProgrammeCount}`} variant="outlined" />
              {companyCounts.map(([companyName, count]) => (
                <Chip
                  key={companyName}
                  label={`${companyName}: ${count}`}
                  variant="outlined"
                />
              ))}
            </Stack>
          </Stack>
        </Paper>

        <Paper sx={{ p: 0 }} elevation={2}>
          {listQuery.isLoading ? (
            <Box display="flex" alignItems="center" gap={1} p={3}>
              <CircularProgress size={20} />
              <Typography variant="body2">Loading participants...</Typography>
            </Box>
          ) : listQuery.isError ? (
            <Alert severity="error" sx={{ m: 2 }}>
              Failed to load participants.
            </Alert>
          ) : filteredParticipants.length ? (
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Participant</TableCell>
                  <TableCell>Linked user</TableCell>
                  <TableCell>Company</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Wear jeopardy</TableCell>
                  <TableCell>Flags</TableCell>
                  <TableCell>Device status</TableCell>
                  <TableCell>Updated</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <TextField
                      size="small"
                      placeholder="Filter"
                      value={headerFilters.participant}
                      onChange={(event) =>
                        setHeaderFilters((prev) => ({
                          ...prev,
                          participant: event.target.value,
                        }))
                      }
                      fullWidth
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      placeholder="Filter"
                      value={headerFilters.linkedUser}
                      onChange={(event) =>
                        setHeaderFilters((prev) => ({
                          ...prev,
                          linkedUser: event.target.value,
                        }))
                      }
                      fullWidth
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      placeholder="Filter"
                      value={headerFilters.company}
                      onChange={(event) =>
                        setHeaderFilters((prev) => ({
                          ...prev,
                          company: event.target.value,
                        }))
                      }
                      fullWidth
                    />
                  </TableCell>
                  <TableCell>
                    <Select
                      size="small"
                      displayEmpty
                      value={headerFilters.status}
                      onChange={(event) =>
                        setHeaderFilters((prev) => ({
                          ...prev,
                          status: event.target.value,
                        }))
                      }
                      fullWidth
                    >
                      <MenuItem value="">All</MenuItem>
                      {STATUS_OPTIONS.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </Select>
                  </TableCell>
                  <TableCell>
                    <Select
                      size="small"
                      displayEmpty
                      value={headerFilters.wearJeopardy}
                      onChange={(event) =>
                        setHeaderFilters((prev) => ({
                          ...prev,
                          wearJeopardy: event.target.value,
                        }))
                      }
                      fullWidth
                    >
                      <MenuItem value="">All</MenuItem>
                      {Object.entries(wearStatusChipStyles).map(([key, config]) => (
                        <MenuItem key={key} value={key}>
                          {config.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </TableCell>
                  <TableCell />
                  <TableCell />
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredParticipants.map((participant) => {
                  const name = `${participant.first_name} ${participant.surname}`.trim();
                  const initials = `${participant.first_name?.[0] ?? ""}${participant.surname?.[0] ?? ""}`
                    .toUpperCase()
                    .slice(0, 2);
                  return (
                    <TableRow
                      key={participant.id}
                      hover
                      sx={{ cursor: "pointer" }}
                      onClick={() =>
                        navigate(`${ROUTES.PARTICIPANTS}/${participant.id}`)
                      }
                    >
                      <TableCell>
                        <Stack direction="row" spacing={2} alignItems="center">
                          <Avatar>{initials || "P"}</Avatar>
                          <Box>
                            <Typography variant="subtitle2">
                              {name || "-"}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {participant.email || "No email"}
                            </Typography>
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {participant.user_email || "Not linked"}
                        </Typography>
                        {(participant.user_first_name || participant.user_last_name) && (
                          <Typography variant="caption" color="text.secondary">
                            {participant.user_first_name} {participant.user_last_name}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{participant.company || "-"}</TableCell>
                      <TableCell>
                        {participant.status ? (
                          <Chip
                            label={participant.status}
                            size="small"
                            sx={getStatusChipSx(participant.status)}
                          />
                        ) : (
                          "-"
                        )}
                      </TableCell>
                      <TableCell>
                        {participant.wear_jeopardy_status ? (
                          <Stack spacing={0.5}>
                            <Chip
                              label={
                                wearStatusChipStyles[participant.wear_jeopardy_status]
                                  ?.label || participant.wear_jeopardy_status
                              }
                              size="small"
                              sx={{
                                backgroundColor:
                                  wearStatusChipStyles[participant.wear_jeopardy_status]?.bg,
                                color:
                                  wearStatusChipStyles[participant.wear_jeopardy_status]?.text,
                                fontWeight: 600,
                              }}
                            />
                            {participant.wear_jeopardy_status_reason && (
                              <Typography variant="caption" color="text.secondary">
                                {participant.wear_jeopardy_status_reason}
                              </Typography>
                            )}
                          </Stack>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1} flexWrap="wrap">
                          {participant.flagged ? (
                            <Chip label={participant.flagged} size="small" />
                          ) : (
                            <Chip label="No flags" size="small" variant="outlined" />
                          )}
                          {participant.color_tag && (
                            <Chip label={participant.color_tag} size="small" />
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                          <Chip
                            label="WHOOP"
                            size="small"
                            variant={participant.whoop_received ? "filled" : "outlined"}
                            sx={
                              participant.whoop_received
                                ? { backgroundColor: "#ecfccb", color: "#365314", fontWeight: 600 }
                                : { color: "text.secondary" }
                            }
                          />
                          <Chip
                            label="Own device"
                            size="small"
                            variant={participant.using_own_device ? "filled" : "outlined"}
                            sx={
                              participant.using_own_device
                                ? { backgroundColor: "#dbeafe", color: "#1e3a8a", fontWeight: 600 }
                                : { color: "text.secondary" }
                            }
                          />
                        </Stack>
                      </TableCell>
                      <TableCell>{formatDateTime(participant.updated_at)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          ) : (
            <Alert severity="info" sx={{ m: 2 }}>
              No participants found.
            </Alert>
          )}
        </Paper>
      </Stack>
    </Box>
  );
};
